/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/modals/templateUpdateFlow.js
 * @class     TemplateUpdateFlow
 * @layer     ui-modal
 * @summary   Coordinates tenant-scoped template updates with confirmation
 *            prompts that mirror the email composer behavior.
 *
 * @description
 * • Loads the raw template source for edit mode and tracks Mustache tokens.
 * • Confirms update intent and removed-token warnings before persisting.
 * • Persists updates to `tenants/{tenantId}/EmailTemplates/{templateId}`.
 *
 * @author    TopSail AI
 * @version   1.0.0
 * @since     2025-09-25
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { docWithTenant, writeWithTenant } from '../utils/firestoreHelpers.js';
import { normalizeEmailAttachments } from '../utils/emailAttachmentUtils.js';

/**
 * Extract all Mustache tokens from a template string.
 * @param {string} input
 * @returns {string[]}
 */
function extractMustacheTokens(input) {
  if (!input) return [];
  return input.match(/{{\s*[^}]+\s*}}/g) ?? [];
}

/**
 * Extract Mustache tokens from subject + HTML sources.
 * @param {{ subject?: string, html?: string }} source
 * @returns {string[]}
 */
function extractTokensFromSource(source) {
  const subjectTokens = extractMustacheTokens(source?.subject || '');
  const htmlTokens = extractMustacheTokens(source?.html || '');
  return [...subjectTokens, ...htmlTokens];
}

/**
 * Determine which Mustache tokens were removed in an edit.
 * @param {string[]} originalTokens
 * @param {string[]} editedTokens
 * @returns {string[]}
 */
function getRemovedMustacheTokens(originalTokens, editedTokens) {
  const original = new Set(originalTokens);
  const edited = new Set(editedTokens);
  return Array.from(original).filter((token) => !edited.has(token));
}

/**
 * Resolve the active tenant id for template updates.
 * @param {object} di
 * @returns {string|null}
 */
function resolveTemplateTenantId(di) {
  return (
    di?.tenant?.getTenantId?.() ||
    di?.auth?.currentUser?.tenantId ||
    di?.auth?.currentUser?.tenant ||
    null
  );
}

/**
 * Fetch the raw template source for editing.
 * @param {object} di
 * @param {string} templateId
 * @param {string|null} tenantId
 * @returns {Promise<{ template: { subject?: string, html?: string, fontFamily?: string, attachments?: unknown[] } | null, error: Error | null }>}
 */
async function getRawTemplate(di, templateId, tenantId) {
  if (!templateId) return { template: null, error: null };
  try {
    if (!tenantId) {
      return {
        template: null,
        error: new Error('Missing tenantId for raw template fetch'),
      };
    }
    if (typeof di?.fsService?.getDocument === 'function') {
      // fsService getDocument is tenant-scoped via docWithTenant.
      // Reads tenants/{tenantId}/EmailTemplates/{templateId}.
      const template = await di.fsService.getDocument(
        `EmailTemplates/${templateId}`,
        false
      );
      return { template, error: null };
    }
    docWithTenant(tenantId, `EmailTemplates/${templateId}`);
    return {
      template: null,
      error: new Error('No Firestore reader available for raw template fetch'),
    };
  } catch (error) {
    return { template: null, error };
  }
}

/**
 * Open a confirmation modal for template updates.
 * @param {{ modalService: { open: (id: string) => void, close: (id: string) => void, register: (id: string, el: HTMLElement) => void }, title: string, message: string, confirmLabel?: string, hostModalId?: string, confirmModalId?: string }} options
 * @returns {Promise<boolean>}
 */
function openTemplateUpdateConfirmModal({
  modalService,
  title,
  message,
  confirmLabel = 'Yes',
  hostModalId = 'emailComposerModal',
  confirmModalId = 'emailTemplateUpdateConfirmModal',
}) {
  const composerHost = document.getElementById(hostModalId);
  const composerWasOpen =
    composerHost && !composerHost.classList.contains('hidden');
  let host = document.getElementById(confirmModalId);
  if (!host) {
    host = document.createElement('div');
    host.id = confirmModalId;
    host.className = 'hidden modal-overlay';
    document.body.appendChild(host);
    modalService.register(confirmModalId, host);
  }

  host.innerHTML = /* html */ `
    <div class="modal-container modal-auto modal-teal p-4 space-y-4 max-w-[28rem]" role="dialog" aria-modal="true">
      <header class="focus-modal__header">
        <div class="focus-modal__title-group">
          <p class="text-xs text-gray-200 mb-1">Confirmation required</p>
          <h2 id="ec-update-title" class="text-lg font-semibold text-white"></h2>
        </div>
      </header>
      <p id="ec-update-message" class="text-sm leading-relaxed text-white"></p>
      <footer class="focus-modal__footer">
        <div class="focus-modal__footer-actions" data-actions="2">
          <button id="ec-update-cancel" class="btn-pill btn--grey btn--info-sm btn--secondary-action">Cancel</button>
          <button id="ec-update-confirm" class="btn-pill btn--orange btn--info-sm btn--primary-action"></button>
        </div>
      </footer>
    </div>
  `;

  const titleEl = host.querySelector('#ec-update-title');
  const messageEl = host.querySelector('#ec-update-message');
  const confirmBtn = host.querySelector('#ec-update-confirm');
  if (titleEl) titleEl.textContent = title;
  if (messageEl) messageEl.textContent = message;
  if (confirmBtn) confirmBtn.textContent = confirmLabel;

  return new Promise((resolve) => {
    const close = (confirmed) => {
      modalService.close(confirmModalId);
      if (composerWasOpen) {
        modalService.open(hostModalId);
      }
      resolve(confirmed);
    };

    const cancelBtn = host.querySelector('#ec-update-cancel');
    confirmBtn?.addEventListener('click', () => close(true), { once: true });
    cancelBtn?.addEventListener('click', () => close(false), { once: true });

    modalService.open(confirmModalId);
    confirmBtn?.focus?.();
  });
}

/**
 * Manage the edit/update flow for tenant email templates.
 */
export class TemplateUpdateFlow {
  /**
   * @param {{
   *   di: object,
   *   modalService: { open: (id: string) => void, close: (id: string) => void, register: (id: string, el: HTMLElement) => void },
   *   templateService?: { render: (id: string, data: object) => Promise<{ subject?: string, html?: string, fontFamily?: string | null }>, renderFromSource?: (source: object, data: object) => Promise<{ subject?: string, html?: string, fontFamily?: string | null }>, invalidate?: (id: string) => void },
   *   templateId?: string,
   *   templateIds?: string[],
   *   templateData?: Record<string, unknown>,
   *   mergeVars?: Record<string, unknown>,
   *   initialSubject?: string,
   *   initialHtml?: string,
   *   initialFontFamily?: string,
   *   defaultFontFamily?: string,
   *   includeSubject?: boolean,
   *   updateImpactLabel?: string,
   *   hostModalId?: string,
   *   confirmModalId?: string,
   *   alertModalId?: string,
   *   normalizeRendered?: (value: string) => string,
   * }} options
   */
  constructor({
    di,
    modalService,
    templateService = di?.templateService,
    templateId = '',
    templateIds = [],
    templateData = {},
    mergeVars = {},
    initialSubject = '',
    initialHtml = '',
    initialFontFamily = '',
    defaultFontFamily = '',
    includeSubject = false,
    updateImpactLabel = 'future emails for this step',
    hostModalId = 'emailComposerModal',
    confirmModalId = 'emailTemplateUpdateConfirmModal',
    alertModalId = '',
    normalizeRendered = (value) => value,
  }) {
    this.di = di;
    this.modalService = modalService;
    this.templateService = templateService;
    this.templateId = templateId || '';
    this.templateIds = Array.isArray(templateIds) ? templateIds : [];
    this.templateData = templateData || {};
    this.mergeVars = mergeVars || {};
    this.includeSubject = includeSubject;
    this.updateImpactLabel = updateImpactLabel || 'future emails for this step';
    this.hostModalId = hostModalId;
    this.confirmModalId = confirmModalId;
    this.alertModalId = alertModalId;
    this.normalizeRendered = normalizeRendered;
    this.rawTemplateHtml = '';
    this.renderedView = {
      subject: initialSubject,
      html: initialHtml,
      fontFamily: initialFontFamily || defaultFontFamily || null,
    };
    this.rawSource = {
      subject: initialSubject,
      html: initialHtml,
      fontFamily: initialFontFamily || defaultFontFamily || null,
    };
    this.rawTokenSnapshot = [];
    this.templateAttachments = [];
    this.updateEnabled = false;
    this.lastError = null;
    this.isEditing = false;
  }

  /**
   * Build the rendering context for template hydration.
   * @returns {Record<string, unknown>}
   */
  getRenderContext() {
    return { ...(this.mergeVars || {}), ...(this.templateData || {}) };
  }

  /**
   * Load a template, its raw source, and its rendered view.
   * @param {string} [templateId]
   * @returns {Promise<{ updateEnabled: boolean, rendered: { subject?: string, html?: string, fontFamily?: string | null }, raw: { subject?: string, html?: string, fontFamily?: string | null }, attachments: Array<object>, renderError?: Error | null }>}
   */
  async loadTemplate(templateId = this.templateId) {
    this.templateId = templateId || '';
    this.lastError = null;
    this.updateEnabled = false;
    this.templateAttachments = [];

    if (!this.templateId) {
      return {
        updateEnabled: false,
        rendered: this.renderedView,
        raw: this.rawSource,
        attachments: [],
      };
    }

    const tenantId = resolveTemplateTenantId(this.di);
    const { template, error } = await getRawTemplate(
      this.di,
      this.templateId,
      tenantId
    );
    this.lastError = error;

    if (error || !template) {
      return {
        updateEnabled: false,
        rendered: this.renderedView,
        raw: this.rawSource,
        attachments: [],
      };
    }

    this.rawSource = {
      subject: typeof template.subject === 'string' ? template.subject : '',
      html: typeof template.html === 'string' ? template.html : '',
      fontFamily:
        typeof template.fontFamily === 'string' ? template.fontFamily : null,
    };
    this.rawTemplateHtml = this.rawSource.html || '';
    this.rawTokenSnapshot = extractTokensFromSource(this.rawSource);
    this.templateAttachments = normalizeEmailAttachments(template.attachments);
    this.updateEnabled = true;

    let renderError = null;
    if (this.templateService?.render) {
      try {
        const rendered = await this.templateService.render(
          this.templateId,
          this.getRenderContext()
        );
        this.renderedView = {
          subject: this.normalizeRendered(rendered.subject || ''),
          html: this.normalizeRendered(rendered.html || ''),
          fontFamily:
            typeof rendered.fontFamily === 'string'
              ? rendered.fontFamily
              : null,
        };
      } catch (error) {
        renderError = error;
        this.lastError = error;
        this.renderedView = {
          subject: this.rawSource.subject,
          html: this.rawSource.html,
          fontFamily: this.rawSource.fontFamily,
        };
      }
    }

    return {
      updateEnabled: this.updateEnabled,
      rendered: this.renderedView,
      raw: this.rawSource,
      attachments: [...this.templateAttachments],
      renderError,
    };
  }

  /**
   * Load the raw template source for the current template.
   * @param {string} [templateId]
   * @returns {Promise<{ updateEnabled: boolean, rawTemplateHtml: string, error: Error | null }>}
   */
  async loadRawTemplate(templateId = this.templateId) {
    this.templateId = templateId || '';
    this.rawTemplateHtml = '';
    this.rawTokenSnapshot = [];
    this.updateEnabled = false;
    this.lastError = null;

    if (!this.templateId) {
      return { updateEnabled: false, rawTemplateHtml: '', error: null };
    }

    const tenantId = resolveTemplateTenantId(this.di);
    const { template, error } = await getRawTemplate(
      this.di,
      this.templateId,
      tenantId
    );
    this.lastError = error;

    if (error || !template) {
      return {
        updateEnabled: false,
        rawTemplateHtml: '',
        error: error || null,
      };
    }

    const rawHtml = typeof template.html === 'string' ? template.html : '';
    this.rawTemplateHtml = rawHtml;
    this.rawTokenSnapshot = extractTokensFromSource({
      subject: template.subject,
      html: rawHtml,
    });
    this.updateEnabled = true;

    return { updateEnabled: true, rawTemplateHtml: rawHtml, error: null };
  }

  /**
   * Return the latest rendered view of the template.
   * @returns {{ subject?: string, html?: string, fontFamily?: string | null }}
   */
  getRenderedView() {
    return this.renderedView;
  }

  /**
   * Return the latest raw source of the template.
   * @returns {{ subject?: string, html?: string, fontFamily?: string | null }}
   */
  getRawSource() {
    return this.rawSource;
  }

  /**
   * Return the latest raw template HTML source.
   * @returns {string}
   */
  getRawTemplateHtml() {
    return this.rawTemplateHtml;
  }

  /**
   * Return the Mustache token snapshot captured at load time.
   * @returns {string[]}
   */
  getTokenSnapshot() {
    return this.rawTokenSnapshot;
  }

  /**
   * Return whether template updates are enabled.
   * @returns {boolean}
   */
  getUpdateEnabled() {
    return this.updateEnabled;
  }

  /**
   * Mark the flow as being in edit mode.
   * @returns {void}
   */
  enterEditMode() {
    this.isEditing = true;
  }

  /**
   * Exit edit mode and record the latest raw source snapshot.
   * @param {{ subject?: string, html?: string, fontFamily?: string }} source
   * @returns {void}
   */
  exitEditMode(source = {}) {
    this.isEditing = false;
    this.rawSource = {
      subject: source.subject ?? this.rawSource.subject ?? '',
      html: source.html ?? this.rawSource.html ?? '',
      fontFamily:
        typeof source.fontFamily === 'string'
          ? source.fontFamily
          : (this.rawSource.fontFamily ?? null),
    };
  }

  /**
   * Confirm and persist a template update to the tenant-scoped collection.
   * @param {{ html?: string, subject?: string, fontFamily?: string, templateId?: string }} options
   * @returns {Promise<{ updated: boolean, reason?: string, templateId?: string, renderError?: Error | null }>}
   */
  async confirmAndUpdate({
    html = '',
    subject = '',
    fontFamily,
    templateId,
  } = {}) {
    const currentTemplateId = templateId || this.templateId;
    if (!currentTemplateId) {
      return { updated: false, reason: 'missing-template-id' };
    }
    if (!this.updateEnabled) {
      return { updated: false, reason: 'update-disabled' };
    }

    const removedTokens = getRemovedMustacheTokens(
      this.rawTokenSnapshot,
      extractTokensFromSource({
        subject: this.includeSubject ? subject : '',
        html,
      })
    );
    if (this.rawTokenSnapshot.length && removedTokens.length) {
      const confirmedRemoval = await openTemplateUpdateConfirmModal({
        modalService: this.modalService,
        title: 'Template variables removed',
        message: `You removed ${removedTokens.length} template variable${removedTokens.length === 1 ? '' : 's'}. Save anyway?`,
        confirmLabel: 'Save anyway',
        hostModalId: this.hostModalId,
        confirmModalId: this.confirmModalId,
      });
      if (!confirmedRemoval) {
        return { updated: false, reason: 'removed-tokens-cancelled' };
      }
    }

    const tenantId = resolveTemplateTenantId(this.di);
    if (!tenantId) {
      return { updated: false, reason: 'missing-tenant-id' };
    }

    const confirmed = await openTemplateUpdateConfirmModal({
      modalService: this.modalService,
      title: 'Update template?',
      message: `This updates the "${currentTemplateId}" template for tenant "${tenantId}" and changes ${this.updateImpactLabel}.`,
      hostModalId: this.hostModalId,
      confirmModalId: this.confirmModalId,
    });
    if (!confirmed) {
      return { updated: false, reason: 'update-cancelled' };
    }

    const ref = docWithTenant(tenantId, `EmailTemplates/${currentTemplateId}`);
    const payload = this.includeSubject
      ? { html, subject, fontFamily: fontFamily ?? null }
      : { html };
    await writeWithTenant(tenantId, ref, payload, { merge: true });
    this.di?.templateService?.invalidate?.(currentTemplateId);
    this.rawTemplateHtml = html;
    this.rawSource = {
      subject: this.includeSubject ? subject : (this.rawSource.subject ?? ''),
      html,
      fontFamily:
        typeof fontFamily === 'string'
          ? fontFamily
          : (this.rawSource.fontFamily ?? null),
    };
    this.rawTokenSnapshot = extractTokensFromSource({
      subject: this.rawSource.subject,
      html: this.rawSource.html,
    });

    let renderError = null;
    if (this.templateService?.renderFromSource) {
      try {
        const rendered = await this.templateService.renderFromSource(
          payload,
          this.getRenderContext()
        );
        this.renderedView = {
          subject: this.normalizeRendered(rendered.subject || ''),
          html: this.normalizeRendered(rendered.html || ''),
          fontFamily:
            typeof rendered.fontFamily === 'string'
              ? rendered.fontFamily
              : null,
        };
      } catch (error) {
        renderError = error;
        this.lastError = error;
        this.renderedView = {
          subject: this.rawSource.subject,
          html: this.rawSource.html,
          fontFamily: this.rawSource.fontFamily,
        };
      }
    }

    return { updated: true, templateId: currentTemplateId, renderError };
  }
}
